var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from 'three';
import { normalizeOrbConfig } from './IOrbConfig';
import { OrbManager } from './OrbManager';
import * as ImageLogic from '../services/creation/ImageLoading';
import * as TextCreation from '../services/creation/TextCreation';
import { stopSpeedTextEffect } from '../services/creation/TextCreation';
import SceneManager from '../core/SceneManager';
import { OrbFactory } from './OrbFactory';
import { global } from '../core/GlobalState';
import { actionRegistry } from '../services/actions/OrbActionRegistry';
import { acquireGeometry, acquireMaterial, releaseGeometry, releaseMaterial } from './OrbResources';
import { positionChildInsideParentVolume } from './OrbContainmentLayout';
import { attachNamedStreamBetweenOrbs } from '../services/networking/OrbStreamAttachments';
// TODO move this to some kind of config
const doImages = true;
export class Orb {
    constructor(orbConfig = null, jsonData = {}, labelText = '' //todo refacotr
    ) {
        var _a, _b, _c, _d;
        this.group = null; // Lazy-loaded 
        this.orbType = null; // Type of the orb, can be set later
        // Speed text fields
        this.speedTextContent = null;
        this.speedTextOptions = {};
        this.speedTextActive = false;
        this.imageUrl = null;
        /** child orbs that this orb spawned because of containmentLayout */
        this.childOrbsManaged = [];
        /** true once we've built containment children for this orb */
        this.containmentInitialized = false;
        this.position = null;
        this.rotationSpeeds = new Map();
        //Might move these
        // === PORTAL FIELDS ===
        this.renderTarget = null;
        this.portalCamera = null;
        this.portalRenderTargetQueued = false;
        // orb action stuff
        this.actions = [];
        this.orbConfig = orbConfig ? normalizeOrbConfig(orbConfig) : null; // ✅ Normalize here
        this.orbType = (_b = (_a = this.orbConfig) === null || _a === void 0 ? void 0 : _a.orbType) !== null && _b !== void 0 ? _b : null;
        this.jsonData = jsonData;
        this.dependentOrbs = new Set();
        this.dependencyOrbs = new Set();
        this.velocity = new THREE.Vector3(0, 0, 0);
        this.text = ((_c = this.orbConfig) === null || _c === void 0 ? void 0 : _c.text) || null;
        this.labelText = labelText; //todo refactor
        this.relationships = (_d = this.orbConfig) === null || _d === void 0 ? void 0 : _d.relationships;
        // Resolve image URL from either direct assignment or dynamic path
        if (orbConfig === null || orbConfig === void 0 ? void 0 : orbConfig.imageUrl) {
            this.imageUrl = orbConfig.imageUrl;
        }
        else if (orbConfig === null || orbConfig === void 0 ? void 0 : orbConfig.imageUrlPath) {
            const extracted = ImageLogic.getUrlValueAtPath(jsonData, orbConfig.imageUrlPath);
            if (typeof extracted === 'string') {
                this.imageUrl = extracted;
                if (orbConfig.imageUrlTemplate) {
                    this.imageUrl = this.resolveImageUrlTemplate(orbConfig.imageUrlTemplate, extracted);
                }
            }
        }
    }
    ensureGroup() {
        if (!this.group) {
            this.group = new THREE.Group();
            if (this.position) {
                this.group.position.copy(this.position);
            }
        }
    }
    resolveImageUrlTemplate(template, extractedValue) {
        return template.replace(/\$\{([^}]+)\}/g, (_match, tokenRaw) => {
            const token = tokenRaw.trim();
            if (token === 'value' || token === 'id') {
                return extractedValue;
            }
            if (token === 'encodedValue' || token === 'encodedId') {
                return encodeURIComponent(extractedValue);
            }
            const encodedPrefix = 'encoded:';
            const shouldEncode = token.startsWith(encodedPrefix);
            const path = shouldEncode ? token.slice(encodedPrefix.length).trim() : token;
            if (!path) {
                return '';
            }
            const resolved = ImageLogic.getUrlValueAtPath(this.jsonData, path);
            if (typeof resolved !== 'string' && typeof resolved !== 'number' && typeof resolved !== 'boolean') {
                return '';
            }
            const text = String(resolved);
            return shouldEncode ? encodeURIComponent(text) : text;
        });
    }
    clearLayerMeshes() {
        var _a, _b, _c;
        if (!this.group)
            return;
        const meshes = this.getLayerMeshes();
        for (const mesh of meshes) {
            const resources = ((_b = (_a = mesh.userData) === null || _a === void 0 ? void 0 : _a.__orbResources) !== null && _b !== void 0 ? _b : null);
            if (resources === null || resources === void 0 ? void 0 : resources.geometryKey) {
                releaseGeometry(resources.geometryKey);
            }
            if (resources === null || resources === void 0 ? void 0 : resources.materialKey) {
                releaseMaterial(resources.materialKey);
            }
            if ((_c = mesh.userData) === null || _c === void 0 ? void 0 : _c.__orbResources) {
                delete mesh.userData.__orbResources;
            }
            this.group.remove(mesh);
        }
        this.rotationSpeeds.clear();
    }
    buildLayerMeshes() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!this.group || !this.orbConfig)
            return;
        const { radius, layers, defaultLayerScaleFactor = 0.08, defaultLayerSide = THREE.BackSide, defaultTransparent = false, defaultOpacity = 1, defaultDepthTest = true, defaultDepthWrite = true, defaultWireframe = false, } = this.orbConfig;
        this.rotationSpeeds.clear();
        for (let i = 0; i < layers.length; i++) {
            const layerConfig = layers[i];
            const shapeType = (_a = layerConfig.shape) !== null && _a !== void 0 ? _a : 'sphere';
            const { geometry, key: geometryKey } = acquireGeometry(shapeType, radius, layerConfig.geometryParams);
            const { material, key: materialKey } = acquireMaterial({
                color: layerConfig.color,
                side: (_b = layerConfig.side) !== null && _b !== void 0 ? _b : defaultLayerSide,
                transparent: (_c = layerConfig.transparent) !== null && _c !== void 0 ? _c : defaultTransparent,
                opacity: (_d = layerConfig.opacity) !== null && _d !== void 0 ? _d : defaultOpacity,
                depthTest: (_e = layerConfig.depthTest) !== null && _e !== void 0 ? _e : defaultDepthTest,
                depthWrite: (_f = layerConfig.depthWrite) !== null && _f !== void 0 ? _f : defaultDepthWrite,
                wireframe: (_g = layerConfig.wireframe) !== null && _g !== void 0 ? _g : defaultWireframe,
            });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.userData.__orbResources = { geometryKey, materialKey };
            mesh.scale.setScalar(1 + i * ((_h = layerConfig.scaleFactor) !== null && _h !== void 0 ? _h : defaultLayerScaleFactor));
            mesh.renderOrder = 1000;
            this.group.add(mesh);
            if (layerConfig.rotationSpeed) {
                this.rotationSpeeds.set(mesh, layerConfig.rotationSpeed);
            }
        }
    }
    rebuildLayerMeshes() {
        if (!this.orbConfig)
            return;
        this.ensureGroup();
        if (!this.group)
            return;
        this.group.scale.setScalar(1);
        this.clearLayerMeshes();
        this.buildLayerMeshes();
        if (this.orbConfig.isPortal) {
            this.disposeWebGLResources();
            this.createPortalRenderTarget();
        }
    }
    //TODO, expand what an orb can be in 3D, such as other types of modles, but also thingsl ike roudnered rectancles, and variously nested rectanbles and orbs that are not always centered
    //TODO see if this can be streamlined any
    /** Creates an orb with layers based on the explicit configuration */
    createOrb() {
        var _a, _b, _c;
        if (!this.orbConfig) {
            throw new Error("Orb cannot be created without a style configuration.");
        }
        const { imageMaskStyle = 'circle', imageSize = 192, imageRadius = 28, } = this.orbConfig;
        this.rebuildLayerMeshes();
        if (!this.group) {
            throw new Error('Orb group failed to initialize.');
        }
        const attachTextFallback = () => {
            if (this.text && !this.group.children.includes(this.text)) {
                this.group.add(this.text);
            }
        };
        // TODO need to consider the case where text and image overlap; add layout or switching logic.
        if (doImages && this.imageUrl) {
            const proxiedUrl = `https://api.orbscape.io/proxyimage?url=${encodeURIComponent(this.imageUrl)}`;
            const originalUrl = this.imageUrl;
            this.imageUrl = proxiedUrl;
            const scaleFactor = (_b = (_a = this.orbConfig) === null || _a === void 0 ? void 0 : _a.imageSpriteScale) !== null && _b !== void 0 ? _b : imageSize * 0.39;
            ImageLogic.attachMaskedSprite(this.group, this.imageUrl, {
                size: imageSize,
                radius: imageRadius,
                maskStyle: imageMaskStyle,
                scale: scaleFactor,
                svgColor: (_c = this.orbConfig) === null || _c === void 0 ? void 0 : _c.imageColor,
                forceSvgDefaultWhite: true,
                originalUrl,
                preferDataUrlForSvg: false,
            }).catch(err => {
                console.warn(`Failed to load image for orb: ${this.imageUrl}`, err);
                attachTextFallback();
            });
        }
        else {
            attachTextFallback();
        }
        return this.group;
    }
    /** Update rotation of layers with rotation styles */
    updateRotation() {
        this.rotationSpeeds.forEach((speed, mesh) => {
            mesh.rotation.x += speed.x;
            mesh.rotation.y += speed.y;
            mesh.rotation.z += speed.z;
        });
    }
    /** Add this Orb to a managed Scene (by instance). */
    addToScene(scene) {
        if (!this.group || this.group.children.length === 0)
            this.createOrb();
        const sm = SceneManager.getInstance();
        const sceneName = sm.getSceneNameByInstance(scene);
        if (!sceneName) {
            console.warn('[Orb] addToScene called with an unmanaged scene. Use scenes from SceneManager.');
            return;
        }
        // Delegate to OrbManager → SceneManager
        OrbManager.getInstance().addOrbToScene(this, sceneName);
        // NEW: if I'm a rootContainer orb, build my internal hierarchy now
        this.initializeContainmentIfNeeded(scene);
    }
    /** Remove this Orb from a specific scene instance. */
    removeFromScene(scene, dispose = false) {
        if (!this.group)
            return;
        // Detach (no disposal)
        OrbManager.getInstance().removeOrb(this.group.uuid, scene);
        // If you also want to free GPU resources, resolve name and dispose via managers
        if (dispose) {
            const sm = SceneManager.getInstance();
            const sceneName = sm.getSceneNameByInstance(scene);
            if (sceneName) {
                OrbManager.getInstance().removeOrbFromScene(this, sceneName, true);
            }
        }
    }
    /** Set the entire orb group's position. */
    setPosition(position) {
        this.position = position; // ← Add a new field to store it
        if (this.group) {
            this.group.position.copy(position);
        }
    }
    /** Update the displayed text (removes old sprite, attaches new). */
    updateText(newText) {
        if (!this.group)
            this.createOrb();
        if (this.text) {
            this.group.remove(this.text);
        }
        this.text = newText;
        this.group.add(newText);
    }
    /** Add a dependency relationship. */
    addDependency(orbId) {
        this.dependencyOrbs.add(orbId);
    }
    /** Add a dependent relationship. */
    addDependent(orbId) {
        this.dependentOrbs.add(orbId);
    }
    /**
   * Attempts to infer the physical radius of this orb from its config.
   * We'll treat the FIRST layer radius as canonical.
   */
    getEffectiveRadius() {
        var _a;
        if (!this.orbConfig)
            return 0;
        return (_a = this.orbConfig.radius) !== null && _a !== void 0 ? _a : 0;
    }
    applyRadiusChange(targetRadius, options) {
        var _a, _b, _c;
        if (!this.orbConfig) {
            return { changed: false, rebuilt: false, radius: targetRadius, previousRadius: targetRadius };
        }
        const previousRadius = (_a = this.orbConfig.radius) !== null && _a !== void 0 ? _a : 0;
        const radius = Math.max(targetRadius, 0.0001);
        if (!isFinite(radius)) {
            return { changed: false, rebuilt: false, radius: previousRadius, previousRadius };
        }
        if (Math.abs(previousRadius - radius) < 1e-6) {
            this.orbConfig.radius = radius;
            return { changed: false, rebuilt: false, radius, previousRadius };
        }
        this.orbConfig.radius = radius;
        if (!this.group) {
            return { changed: true, rebuilt: false, radius, previousRadius };
        }
        const configThreshold = this.orbConfig.resizeRebuildRatio;
        const threshold = Math.max((_c = (_b = options === null || options === void 0 ? void 0 : options.rebuildThresholdRatio) !== null && _b !== void 0 ? _b : (configThreshold && configThreshold > 1 ? configThreshold : 0)) !== null && _c !== void 0 ? _c : 1.6, 1.01);
        const inverseThreshold = 1 / threshold;
        const ratio = previousRadius > 0 ? radius / previousRadius : Number.POSITIVE_INFINITY;
        const needsRebuild = !isFinite(ratio) ||
            ratio <= 0 ||
            previousRadius <= 0 ||
            ratio > threshold ||
            ratio < inverseThreshold;
        if (needsRebuild) {
            this.rebuildLayerMeshes();
            return { changed: true, rebuilt: true, radius, previousRadius };
        }
        this.group.scale.multiplyScalar(ratio);
        return { changed: true, rebuilt: false, radius, previousRadius };
    }
    /**
   * Spawns & positions nested child orbs per this.orbConfig.containmentLayout.
   * Safe to call multiple times; only runs once.
   */
    initializeContainmentIfNeeded(scene) {
        var _a;
        if (this.containmentInitialized)
            return;
        if (!this.orbConfig)
            return;
        if (this.orbConfig.layoutMode !== 'rootContainer')
            return;
        if (!((_a = this.orbConfig.containmentLayout) === null || _a === void 0 ? void 0 : _a.enabled))
            return;
        if (!this.group)
            this.createOrb();
        if (!this.group)
            return; // still sanity
        const layoutSpec = this.orbConfig.containmentLayout;
        const parentRadius = this.getEffectiveRadius();
        // recursively build children
        const buildChildrenRecursive = (parentOrb, parentRadius, specs, depth) => {
            specs.forEach((spec, idx) => {
                var _a;
                // 1. make a child orb instance
                const childOrb = OrbFactory.createOrbByType(spec.orbType, (_a = spec.label) !== null && _a !== void 0 ? _a : spec.orbType);
                // mark as "contained"
                if (!childOrb.orbConfig)
                    childOrb.orbConfig = {};
                childOrb.orbConfig.layoutMode = 'containedChild';
                // shrink radius if requested
                if (spec.relativeRadiusRatio && childOrb.orbConfig) {
                    childOrb.orbConfig.radius = parentRadius * spec.relativeRadiusRatio;
                    // also shrink layer geometries by hacking defaultLayerScaleFactor
                    // so the orb visually matches that new radius.
                    // (Optional; if your OrbFactory already uses radius at creation,
                    //  then you're fine and can skip this block.)
                }
                // actually create the THREE.Group for the orb now
                const childGroup = childOrb.createOrb();
                // we DO NOT add to global scene root.
                // we parent to the parentOrb.group, so transforms are nested.
                parentOrb.group.add(childGroup);
                // register graph relationship:
                parentOrb.addDependent(childOrb.group.uuid);
                childOrb.addDependency(parentOrb.group.uuid);
                // local positioning inside parent:
                positionChildInsideParentVolume({
                    parentOrb,
                    childOrb,
                    layoutMode: layoutSpec.mode,
                    spec,
                    indexInSiblings: idx,
                    depth,
                });
                // track
                parentOrb.childOrbsManaged.push(childOrb);
                // recurse
                if (spec.children && spec.children.length > 0) {
                    buildChildrenRecursive(childOrb, childOrb.getEffectiveRadius(), spec.children, depth + 1);
                }
            });
        };
        buildChildrenRecursive(this, parentRadius, layoutSpec.children, 0);
        this.containmentInitialized = true;
    }
    /** Display JSON data to the console. */
    displayJson() {
        let jsonData = JSON.stringify(this.jsonData, null, 2);
        console.log(jsonData);
        return jsonData;
    }
    /** Apply a transformation function to the position. */
    // I am sus of this, I don't want to check if the group exists every frame but I don't know how it could be safely avoided without likely separating somethign out
    applyTransformation(func) {
        if (!this.group)
            this.createOrb();
        this.group.position.copy(func(this.group.position));
    }
    /** Update position based on velocity. */
    updatePosition() {
        const velocitySq = this.velocity.lengthSq();
        if (velocitySq < Math.pow(Orb.VELOCITY_EPSILON, 2)) {
            this.velocity.set(0, 0, 0);
            return;
        }
        if (!this.group)
            this.createOrb();
        this.group.position.add(this.velocity);
        if (this.group.position.y <= 0) {
            this.velocity.reflect(new THREE.Vector3(0, 1, 0));
        }
        if (velocitySq > Math.pow(Orb.VELOCITY_EPSILON, 2)) {
            this.velocity.multiplyScalar(0.999); // Apply drag
        }
    }
    /** Add velocity to the orb. */
    addVelocity(velocity) {
        this.velocity.add(velocity);
    }
    setVelocity(velocity) {
        this.velocity.copy(velocity);
    }
    enableSpeedText(options = {}) {
        var _a;
        this.speedTextContent = this.labelText; // ✅ Automatically use labelText
        this.speedTextOptions = options;
        this.speedTextActive = true;
        if (!this.group)
            this.createOrb();
        if (!this.text) {
            this.text = TextCreation.createTextSprite("", 2, new THREE.Vector3(0, 0, 0));
            this.group.add(this.text);
        }
        this.text.position.set(0, 0, 0);
        //make this a switch, should be able to dyanmically turn this on and off, say after looking near an orb
        stopSpeedTextEffect(this.text);
        TextCreation.startSpeedTextEffect(this.text, this.labelText, Object.assign({ wpm: (_a = options.wpm) !== null && _a !== void 0 ? _a : 300 }, options));
    }
    disableSpeedText() {
        this.speedTextActive = false;
        if (this.text) {
            stopSpeedTextEffect(this.text);
            this.updateText(TextCreation.createTextSprite(this.labelText, 2, new THREE.Vector3(0, 0, 0)));
        }
    }
    toggleSpeedText(options = {}) {
        if (this.speedTextActive) {
            this.disableSpeedText();
        }
        else {
            this.enableSpeedText(options);
        }
    }
    // --- helpers ---
    getLayerMeshes() {
        if (!this.group)
            return [];
        return this.group.children.filter(c => c instanceof THREE.Mesh);
    }
    getInnermostLayer() {
        const meshes = this.getLayerMeshes();
        return meshes.length ? meshes[0] : null;
    }
    getOutermostLayer() {
        const meshes = this.getLayerMeshes();
        return meshes.length ? meshes[meshes.length - 1] : null;
    }
    getPortalLayerMesh() {
        var _a, _b, _c, _d, _e;
        const meshes = this.group ? this.group.children.filter(m => m instanceof THREE.Mesh) : [];
        if (!meshes.length)
            return null;
        // Prefer explicit portalSurface flag if provided
        for (let i = 0; i < meshes.length; i++) {
            if ((_c = (_b = (_a = this.orbConfig) === null || _a === void 0 ? void 0 : _a.layers) === null || _b === void 0 ? void 0 : _b[i]) === null || _c === void 0 ? void 0 : _c.portalSurface)
                return meshes[i];
        }
        // Fallback: inner / outer / index
        const selector = (_e = (_d = this.orbConfig) === null || _d === void 0 ? void 0 : _d.portalLayer) !== null && _e !== void 0 ? _e : 'inner';
        if (selector === 'inner')
            return meshes[0];
        if (selector === 'outer')
            return meshes[meshes.length - 1];
        const idx = Math.max(0, Math.min(meshes.length - 1, selector | 0));
        return meshes[idx];
    }
    buildPortalRenderTarget() {
        var _a, _b, _c, _d;
        const res = this.orbConfig.portalResolution || 512;
        if (this.renderTarget) {
            this.renderTarget.dispose();
            this.renderTarget = null;
        }
        this.renderTarget = new THREE.WebGLRenderTarget(res, res, {
            depthBuffer: true,
            stencilBuffer: false,
        });
        this.portalCamera = new THREE.PerspectiveCamera(this.orbConfig.portalCameraFOV || 70, 1, 0.1, 50000);
        const portalMesh = this.getPortalLayerMesh();
        if (!portalMesh)
            return;
        // IMPORTANT:
        // - Do NOT texture this mesh.
        // - It is invisible in the base pass and used only to write stencil in the overlay.
        portalMesh.visible = false;
        portalMesh.renderOrder = 2000; // keep stable ordering relative to siblings
        if ((_b = (_a = this.portalWindowMaterial) === null || _a === void 0 ? void 0 : _a.uniforms) === null || _b === void 0 ? void 0 : _b.tScene) {
            this.portalWindowMaterial.uniforms.tScene.value = (_d = (_c = this.renderTarget) === null || _c === void 0 ? void 0 : _c.texture) !== null && _d !== void 0 ? _d : null;
            this.portalWindowMaterial.needsUpdate = true;
        }
    }
    createPortalRenderTarget() {
        var _a, _b, _c;
        if (this.portalRenderTargetQueued)
            return;
        const scheduleUpload = (_a = global.highPerformanceRuntime) === null || _a === void 0 ? void 0 : _a.enqueueGpuUpload;
        if (scheduleUpload) {
            this.portalRenderTargetQueued = true;
            scheduleUpload(`portal-rt:${(_c = (_b = this.group) === null || _b === void 0 ? void 0 : _b.uuid) !== null && _c !== void 0 ? _c : 'orb'}`, () => {
                this.portalRenderTargetQueued = false;
                this.buildPortalRenderTarget();
            });
            return;
        }
        this.buildPortalRenderTarget();
    }
    recreateWebGLResources(_renderer) {
        var _a;
        if ((_a = this.orbConfig) === null || _a === void 0 ? void 0 : _a.isPortal) {
            this.createPortalRenderTarget();
        }
        // Ensure sprite/text textures are re-uploaded
        if (this.text && this.text.material instanceof THREE.SpriteMaterial) {
            const mat = this.text.material;
            if (mat.map) {
                mat.map.needsUpdate = true;
            }
            mat.needsUpdate = true;
        }
        if (this.group) {
            this.group.traverse((child) => {
                const material = child.material;
                if (!material)
                    return;
                const markNeedsUpdate = (mat) => {
                    if (mat.map) {
                        mat.map.needsUpdate = true;
                    }
                    mat.needsUpdate = true;
                };
                if (Array.isArray(material))
                    material.forEach(markNeedsUpdate);
                else
                    markNeedsUpdate(material);
            });
        }
    }
    disposeWebGLResources() {
        var _a, _b;
        if (this.renderTarget) {
            this.renderTarget.dispose();
            this.renderTarget = null;
        }
        if ((_b = (_a = this.portalWindowMaterial) === null || _a === void 0 ? void 0 : _a.uniforms) === null || _b === void 0 ? void 0 : _b.tScene) {
            this.portalWindowMaterial.uniforms.tScene.value = null;
            this.portalWindowMaterial.needsUpdate = true;
        }
        this.portalCamera = null;
        this.portalRenderTargetQueued = false;
    }
    attachStream(targetOrb, streamName = 'traffic') {
        var _a, _b;
        const sourceScene = (_a = this.group) === null || _a === void 0 ? void 0 : _a.parent;
        const targetScene = (_b = targetOrb.group) === null || _b === void 0 ? void 0 : _b.parent;
        const scene = sourceScene instanceof THREE.Scene
            ? sourceScene
            : targetScene instanceof THREE.Scene
                ? targetScene
                : undefined;
        if (!scene) {
            return false;
        }
        return attachNamedStreamBetweenOrbs(streamName, scene, this, targetOrb);
    }
    // action stuff
    addAction(action) {
        this.actions.push(action);
    }
    runAction(trigger) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const manager = OrbManager.getInstance();
            const sm = SceneManager.getInstance();
            for (const action of this.actions) {
                if (action.trigger !== trigger)
                    continue;
                const kind = (_a = action.type) !== null && _a !== void 0 ? _a : action.kind; // support old 'type'
                const params = (_c = (_b = action.options) !== null && _b !== void 0 ? _b : action.params) !== null && _c !== void 0 ? _c : {};
                const handler = actionRegistry.get(kind);
                if (!handler) {
                    console.warn(`No handler registered for action: ${kind}`);
                    continue;
                }
                try {
                    // Resolve a THREE.Scene for the action context
                    let scene;
                    // 1) direct parent if available
                    if (((_d = this.group) === null || _d === void 0 ? void 0 : _d.parent) && this.group.parent instanceof THREE.Scene) {
                        scene = this.group.parent;
                    }
                    else {
                        // 2) lookup via OrbManager -> SceneManager (managed scenes)
                        const sceneName = manager.getSceneForOrb(this);
                        scene = sceneName ? sm.getScene(sceneName) : sm.getScene(); // 3) fallback to current default
                    }
                    yield handler({ orb: this, manager, factory: OrbFactory, scene }, params);
                }
                catch (e) {
                    console.error(`Error running action ${kind}:`, e);
                }
            }
        });
    }
}
Orb.VELOCITY_EPSILON = 1e-6;
